import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../hooks/useToast';
import {
  InstitutionDto,
  getInstitutionDisplayName,
  AdministrativeStatus,
  normalizeCommunicationLanguage,
} from '../types/institution';
import { CommunicationLanguage } from '../types/auth';
import { Title } from '../types/member';
import { adminService } from '../services/admin';
import { formatImportProblems } from '../utils/importProblems';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Avatar } from '../components/common/Avatar';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { animations } from '../design-system/tokens/animations';
import { headerWatermarkStyles } from '../components/dashboard/dashboardWatermark';
import { MonumentCaption } from '../components/dashboard/MonumentCaption';
import { useMonumentWatermark } from '../hooks/useMonumentWatermark';
import { getMembershipCategoryLabel } from '../utils/institutionDashboardViewModel';
import { formatInstitutionTypeLabel } from '../data/icomInstitutionTypes';
import { formatAthensDate } from '../utils/athensTime';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
  width: 100%;
  max-width: 1180px;
`;

const IdentityCard = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 180px;
  display: flex;
  align-items: center;
  padding: ${spacing[8]};
  margin-bottom: ${spacing[2]};
  border-radius: ${borders.radius.lg};
  background: transparent;
  ${headerWatermarkStyles}

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[4]};
    min-height: auto;
  }
`;

const AccentBar = styled.span`
  display: block;
  width: 58px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.theme.colors.accent};
  margin-bottom: ${spacing[4]};
`;

const IdentityInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[5]};
`;

const IdentityMain = styled.div`
  display: flex;
  gap: ${spacing[4]};
  min-width: 0;
  flex: 1;
`;

const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  min-width: 0;
`;

const Name = styled.h1`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-size: clamp(1.4rem, 2.1vw, 1.9rem);
  font-weight: ${typography.fontWeight.semibold};
  line-height: 1.25;
  color: ${props => props.theme.colors.text};
  letter-spacing: ${typography.letterSpacing.tighter};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]} ${spacing[4]};
  align-items: center;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${props => props.theme.colors.primary};
  }

  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: ${props => props.theme.colors.primary};
      text-decoration: underline;
    }
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[1]};
`;

const SoftBadge = styled.span<{ $tone?: 'warning' | 'neutral' | 'accent' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: ${typography.letterSpacing.wide};
  text-transform: uppercase;
  background: ${props => {
    if (props.$tone === 'warning') return `${props.theme.colors.warning}18`;
    if (props.$tone === 'accent') return `${props.theme.colors.primary}12`;
    return props.theme.colors.surfaceHover;
  }};
  color: ${props => {
    if (props.$tone === 'warning') return props.theme.colors.warning;
    if (props.$tone === 'accent') return props.theme.colors.primary;
    return props.theme.colors.textSecondary;
  }};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: ${spacing[6]};
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SideNav = styled(Card)`
  position: sticky;
  top: ${spacing[6]};
  padding: ${spacing[3]} !important;

  @media (max-width: ${breakpoints.md}) {
    position: relative;
    top: 0;
  }
`;

const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};

  @media (max-width: ${breakpoints.md}) {
    flex-direction: row;
    overflow-x: auto;
    gap: ${spacing[2]};
  }
`;

const NavButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  min-height: 44px;
  padding: ${spacing[3]} ${spacing[4]};
  border: none;
  border-radius: ${borders.radius.md};
  background: ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.$active ? props.theme.colors.surface : props.theme.colors.text)};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${props =>
    props.$active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  text-align: left;
  cursor: pointer;
  transition: ${animations.transition(
    ['background-color', 'color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};

  &:hover {
    background: ${props =>
      props.$active ? props.theme.colors.primaryDark : props.theme.colors.surfaceHover};
  }

  @media (max-width: ${breakpoints.md}) {
    width: auto;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const SectionCard = styled(Card)``;

const SectionTitle = styled(Heading)`
  margin: 0 0 ${spacing[5]} 0 !important;
  font-family: ${typography.fontFamily.display};
  letter-spacing: ${typography.letterSpacing.tighter};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing[5]};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

const InfoLabel = styled(Text)`
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-size: ${typography.fontSize.xs};
`;

const EmptyValue = styled(Text)`
  font-style: italic;
  color: ${props => props.theme.colors.textMuted};
`;

const CommitteeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const CommitteeCard = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border: ${borders.width.thin} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  border-left: 3px solid ${props => props.theme.colors.primary};
`;

const CommitteeName = styled.div`
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.base};
  margin-bottom: ${spacing[1]};
`;

const CommitteeCode = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
`;

type ProfileSection =
  | 'institution'
  | 'contact'
  | 'leadership'
  | 'membership'
  | 'committees';

function bilingual(en?: string | null, gr?: string | null): string | null {
  if (!en && !gr) return null;
  if (en && gr && en !== gr) return `${en} / ${gr}`;
  return en || gr || null;
}

const InstitutionProfileView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const watermark = useMonumentWatermark();
  const { error: showError, success: showSuccess } = useToast();
  const [institution, setInstitution] = useState<InstitutionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<ProfileSection>('institution');
  const [accountActionOpen, setAccountActionOpen] = useState(false);
  const [accountActionLoading, setAccountActionLoading] = useState(false);

  useEffect(() => {
    if (id) loadInstitution();
  }, [id]);

  const loadInstitution = async () => {
    if (!id) return;
    setLoading(true);
    try {
      setInstitution(await adminService.getInstitutionById(id));
    } catch (error) {
      showError(error instanceof Error ? error.message : t('profile.institutionLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccountAction = async () => {
    if (!id || !institution) return;
    setAccountActionLoading(true);
    try {
      if (institution.isActive !== false) {
        await adminService.deactivateInstitution(id);
        setInstitution(prev => (prev ? { ...prev, isActive: false } : null));
        showSuccess(t('admin.institutionAccount.deactivateSuccess'));
      } else {
        await adminService.reactivateInstitution(id);
        setInstitution(prev => (prev ? { ...prev, isActive: true } : null));
        showSuccess(t('admin.institutionAccount.reactivateSuccess'));
      }
      setAccountActionOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setAccountActionLoading(false);
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return formatAthensDate(dateString, i18n.language);
  };

  const getTitleLabel = (title?: number | null): string | null => {
    if (title == null) return null;
    const labels: Record<number, string> = {
      [Title.Mr]: t('register.titleMr'),
      [Title.Ms]: t('register.titleMs'),
      [Title.Mrs]: t('register.titleMrs'),
      [Title.Dr]: t('register.titleDr'),
      [Title.Prof]: t('register.titleProf'),
      [Title.Other]: t('register.titleOther'),
    };
    return labels[title] || null;
  };

  const getCommunicationLanguageLabel = (
    value?: CommunicationLanguage | number | string | null
  ): string | null => {
    const lang = normalizeCommunicationLanguage(value);
    if (lang === CommunicationLanguage.English) return t('register.languageEnglish');
    if (lang === CommunicationLanguage.Greek) return t('register.languageGreek');
    if (lang === CommunicationLanguage.French) return t('register.languageFrench');
    return null;
  };

  const getAdministrativeStatusLabel = (status?: AdministrativeStatus): string => {
    if (!status) return '-';
    const labels: Record<AdministrativeStatus, string> = {
      [AdministrativeStatus.Public]: t('institution.status.public'),
      [AdministrativeStatus.Private]: t('institution.status.private'),
      [AdministrativeStatus.Mixed]: t('institution.status.mixed'),
    };
    return labels[status] || 'Unknown';
  };

  const renderValue = (value?: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      return <EmptyValue size="base">—</EmptyValue>;
    }
    return <Text size="base" weight="medium">{value}</Text>;
  };

  if (loading) return <LoadingSpinner fullPage />;
  if (!institution) return <div>{t('institution.notFound')}</div>;

  const displayName = getInstitutionDisplayName(institution);
  const email = institution.institutionEmail || institution.contactPersonEmail;
  const phone = institution.institutionPhone || institution.contactPersonPhone;

  const sections: { id: ProfileSection; label: string }[] = [
    { id: 'institution', label: t('institution.sections.institution') },
    { id: 'contact', label: t('institution.sections.contact') },
    { id: 'leadership', label: t('institution.sections.leadership') },
    { id: 'membership', label: t('institution.sections.membership') },
    { id: 'committees', label: t('institution.sections.committees') },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'institution':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('institution.sections.institution')}
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.name')}</InfoLabel>
                {renderValue(displayName)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.foundationDate')}</InfoLabel>
                {renderValue(institution.foundationDate ? formatDate(institution.foundationDate) : null)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.website')}</InfoLabel>
                {institution.website ? (
                  <Text size="base" weight="medium">
                    <a href={institution.website} target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary }}>
                      {institution.website}
                    </a>
                  </Text>
                ) : (
                  <EmptyValue size="base">—</EmptyValue>
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.membershipCategory')}</InfoLabel>
                {renderValue(getMembershipCategoryLabel(institution.membershipCategory, t))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.administrativeStatusLabel')}</InfoLabel>
                {renderValue(getAdministrativeStatusLabel(institution.administrativeStatus))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.institutionType')}</InfoLabel>
                {renderValue(
                  institution.institutionType && institution.institutionType.length > 0
                    ? institution.institutionType
                        .map(v => formatInstitutionTypeLabel(v, t))
                        .join(', ')
                    : null
                )}
              </InfoItem>
              {institution.collectionTypes && institution.collectionTypes.length > 0 && (
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.collectionTypes')}</InfoLabel>
                {renderValue(institution.collectionTypes.join(', '))}
              </InfoItem>
              )}
              {institution.importProblems && (
                <InfoItem style={{ gridColumn: '1 / -1' }}>
                  <InfoLabel size="xs" color="secondary">{t('admin.excelImport.importProblems')}</InfoLabel>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', marginTop: spacing[1] }}>
                    {formatImportProblems(institution.importProblems, t)}
                  </Text>
                  <Text size="xs" color="secondary" style={{ marginTop: spacing[1] }}>
                    {t('admin.excelImport.importProblemsHint')}
                  </Text>
                </InfoItem>
              )}
            </InfoGrid>
          </SectionCard>
        );
      case 'contact':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('institution.sections.contact')}
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.address')}</InfoLabel>
                {renderValue(
                  bilingual(institution.primaryAddress?.addressEn, institution.primaryAddress?.addressGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.city')}</InfoLabel>
                {renderValue(
                  bilingual(institution.primaryAddress?.cityEn, institution.primaryAddress?.cityGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.postalCode')}</InfoLabel>
                {renderValue(
                  bilingual(institution.primaryAddress?.postalCodeEn, institution.primaryAddress?.postalCodeGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.country')}</InfoLabel>
                {renderValue(
                  bilingual(institution.primaryAddress?.countryEn, institution.primaryAddress?.countryGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.email')}</InfoLabel>
                {renderValue(institution.institutionEmail)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.phone')}</InfoLabel>
                {renderValue(institution.institutionPhone)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.contactPerson')}</InfoLabel>
                {renderValue(
                  [institution.contactPersonFirstName, institution.contactPersonLastName]
                    .filter(Boolean)
                    .join(' ') || null
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.contactPersonEmail')}</InfoLabel>
                {renderValue(institution.contactPersonEmail)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.contactPersonPhone')}</InfoLabel>
                {renderValue(institution.contactPersonPhone)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('register.secondaryAddress')}</InfoLabel>
                {renderValue(
                  bilingual(institution.secondaryAddress?.addressEn, institution.secondaryAddress?.addressGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.city')}</InfoLabel>
                {renderValue(
                  bilingual(institution.secondaryAddress?.cityEn, institution.secondaryAddress?.cityGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.postalCode')}</InfoLabel>
                {renderValue(
                  bilingual(
                    institution.secondaryAddress?.postalCodeEn,
                    institution.secondaryAddress?.postalCodeGr
                  )
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.country')}</InfoLabel>
                {renderValue(
                  bilingual(institution.secondaryAddress?.countryEn, institution.secondaryAddress?.countryGr)
                )}
              </InfoItem>
            </InfoGrid>
          </SectionCard>
        );
      case 'leadership':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('institution.sections.leadership')}
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('register.titleOption')}</InfoLabel>
                {renderValue(getTitleLabel(institution.directorTitle))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.dateOfBirth')}</InfoLabel>
                {renderValue(
                  institution.directorDateOfBirth ? formatDate(institution.directorDateOfBirth) : null
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.director')}</InfoLabel>
                {renderValue(
                  bilingual(
                    [institution.directorName?.firstNameEn, institution.directorName?.lastNameEn]
                      .filter(Boolean)
                      .join(' '),
                    [institution.directorName?.firstNameGr, institution.directorName?.lastNameGr]
                      .filter(Boolean)
                      .join(' ')
                  )
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.directorEmail')}</InfoLabel>
                {renderValue(institution.directorEmail)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.directorPhone')}</InfoLabel>
                {renderValue(institution.directorPhone)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('register.titleOption')}</InfoLabel>
                {renderValue(getTitleLabel(institution.icomRepresentativeTitle))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.dateOfBirth')}</InfoLabel>
                {renderValue(
                  institution.icomRepresentativeDateOfBirth
                    ? formatDate(institution.icomRepresentativeDateOfBirth)
                    : null
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.icomRepresentative')}</InfoLabel>
                {renderValue(
                  bilingual(
                    [
                      institution.icomRepresentativeName?.firstNameEn,
                      institution.icomRepresentativeName?.lastNameEn,
                    ]
                      .filter(Boolean)
                      .join(' '),
                    [
                      institution.icomRepresentativeName?.firstNameGr,
                      institution.icomRepresentativeName?.lastNameGr,
                    ]
                      .filter(Boolean)
                      .join(' ')
                  )
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.icomRepresentativePosition')}</InfoLabel>
                {renderValue(institution.icomRepresentativePosition)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.icomRepresentativeEmail')}</InfoLabel>
                {renderValue(institution.icomRepresentativeEmail)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.icomRepresentativePhone')}</InfoLabel>
                {renderValue(institution.icomRepresentativePhone)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('register.languageForCommunication')}</InfoLabel>
                {renderValue(getCommunicationLanguageLabel(
                  institution.icomRepresentativeCommunicationLanguage
                ))}
              </InfoItem>
            </InfoGrid>
          </SectionCard>
        );
      case 'membership':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('institution.sections.membership')}
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.membershipCategory')}</InfoLabel>
                {renderValue(getMembershipCategoryLabel(institution.membershipCategory, t))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.numberOfMemberCards')}</InfoLabel>
                {renderValue(institution.numberOfMemberCards)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('institution.registrationYear')}</InfoLabel>
                {renderValue(institution.registrationYear)}
              </InfoItem>
            </InfoGrid>
          </SectionCard>
        );
      case 'committees':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('institution.sections.committees')}
            </SectionTitle>
            {!institution.committees || institution.committees.length === 0 ? (
              <EmptyValue size="base">—</EmptyValue>
            ) : (
              <CommitteeList>
                {institution.committees.map(committee => {
                  const repName = bilingual(
                    [committee.name?.firstNameEn, committee.name?.lastNameEn]
                      .filter(Boolean)
                      .join(' '),
                    [committee.name?.firstNameGr, committee.name?.lastNameGr]
                      .filter(Boolean)
                      .join(' ')
                  );
                  const position = bilingual(
                    committee.representativePositionEn,
                    committee.representativePositionGr
                  ) || committee.representativePosition;
                  return (
                    <CommitteeCard key={committee.id}>
                      <CommitteeName>
                        {committee.committee?.nameEn || committee.committee?.nameGr || '—'}
                      </CommitteeName>
                      <CommitteeCode>
                        {t('institution.code')}: {committee.committee?.code || '—'}
                        {committee.voteNumber != null && (
                          <> · {t('profile.voteNumberLabel', { n: committee.voteNumber })}</>
                        )}
                      </CommitteeCode>
                      {repName && (
                        <CommitteeCode>
                          {t('register.icomRepresentative')}: {repName}
                          {position ? ` · ${position}` : ''}
                          {committee.representativeEmail ? ` · ${committee.representativeEmail}` : ''}
                        </CommitteeCode>
                      )}
                    </CommitteeCard>
                  );
                })}
              </CommitteeList>
            )}
          </SectionCard>
        );
      default:
        return null;
    }
  };

  return (
    <Page>
      <IdentityCard style={watermark.style}>
        <IdentityInner>
          <div style={{ width: '100%' }}>
            <AccentBar aria-hidden />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: spacing[5],
                width: '100%',
              }}
            >
          <IdentityMain>
            <Avatar name={displayName} size="xl" border borderColor="primary" />
            <IdentityText>
              <Name>{displayName}</Name>
              <MetaRow>
                {email && (
                  <MetaItem>
                    <EnvelopeIcon aria-hidden />
                    {email}
                  </MetaItem>
                )}
                {phone && (
                  <MetaItem>
                    <PhoneIcon aria-hidden />
                    {phone}
                  </MetaItem>
                )}
                {institution.website && (
                  <MetaItem>
                    <GlobeAltIcon aria-hidden />
                    <a href={institution.website} target="_blank" rel="noopener noreferrer">
                      {institution.website.replace(/^https?:\/\//, '')}
                    </a>
                  </MetaItem>
                )}
              </MetaRow>
              <BadgeRow>
                <SoftBadge $tone="accent">
                  {getMembershipCategoryLabel(institution.membershipCategory, t)}
                </SoftBadge>
                {institution.administrativeStatus && (
                  <SoftBadge>
                    {getAdministrativeStatusLabel(institution.administrativeStatus)}
                  </SoftBadge>
                )}
                {institution.isActive === false && (
                  <SoftBadge $tone="warning">
                    {t('admin.institutionAccount.accountInactive')}
                  </SoftBadge>
                )}
              </BadgeRow>
            </IdentityText>
          </IdentityMain>

          <Actions>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/institutions')}>
              <ArrowLeftIcon style={{ width: 16, height: 16, marginRight: 6 }} />
              {t('common.back')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => id && navigate(`/admin/institutions/${id}/edit`)}
            >
              <PencilSquareIcon style={{ width: 16, height: 16, marginRight: 6 }} />
              {t('admin.member.edit')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAccountActionOpen(true)}
            >
              {institution.isActive === false
                ? t('admin.institutionAccount.reactivate')
                : t('admin.institutionAccount.deactivate')}
            </Button>
          </Actions>
            </div>
          </div>
        </IdentityInner>
        <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
      </IdentityCard>

      <Layout>
        <SideNav variant="elevated" pattern="dots" patternIntensity="subtle">
          <NavList aria-label={t('institution.sections.institution')}>
            {sections.map(section => (
              <NavButton
                key={section.id}
                type="button"
                $active={activeSection === section.id}
                onClick={() => setActiveSection(section.id)}
                aria-current={activeSection === section.id ? 'page' : undefined}
              >
                {section.label}
              </NavButton>
            ))}
          </NavList>
        </SideNav>
        <div>{renderSection()}</div>
      </Layout>

      <ConfirmDialog
        isOpen={accountActionOpen}
        onClose={() => !accountActionLoading && setAccountActionOpen(false)}
        onConfirm={handleAccountAction}
        title={
          institution.isActive === false
            ? t('admin.institutionAccount.reactivate')
            : t('admin.institutionAccount.deactivate')
        }
        message={
          institution.isActive === false
            ? t('admin.institutionAccount.reactivateConfirm')
            : t('admin.institutionAccount.deactivateConfirm')
        }
        confirmText={
          institution.isActive === false
            ? t('admin.institutionAccount.reactivate')
            : t('admin.institutionAccount.deactivate')
        }
        variant={institution.isActive === false ? 'primary' : 'danger'}
      />
    </Page>
  );
};

export default InstitutionProfileView;
